import { useCallback, useEffect } from "react";

import { useAtom, useSetAtom } from "jotai";

import {
  browsingCountryAtom,
  countryHydratedAtom,
  persistBrowsingCountry,
  readBrowsingCountryFromCookie,
} from "@/state/country";

interface UseBrowsingCountry {
  /** lowercase ISO 3166-1 alpha-2; null until hydrated AND no cookie set */
  country: string | null;
  /** true after the first hydration tick; gate UI on this */
  isHydrated: boolean;
  /** persist + update atom in one call */
  setCountry: (next: string) => void;
}

/**
 * Read/write the browsing country with cookie persistence on `.pontimed.app`.
 * Hydration runs once on mount of the first consumer (idempotent — guarded
 * by countryHydratedAtom).
 */
export function useBrowsingCountry(): UseBrowsingCountry {
  const [country, setCountryAtom] = useAtom(browsingCountryAtom);
  const [isHydrated, setIsHydrated] = useAtom(countryHydratedAtom);

  useEffect(() => {
    if (isHydrated) return;
    const fromCookie = readBrowsingCountryFromCookie();
    if (fromCookie) setCountryAtom(fromCookie);
    setIsHydrated(true);
  }, [isHydrated, setCountryAtom, setIsHydrated]);

  const setCountry = useCallback(
    (next: string) => {
      const normalized = persistBrowsingCountry(next);
      setCountryAtom(normalized);
    },
    [setCountryAtom],
  );

  return { country, isHydrated, setCountry };
}

/**
 * Setter-only variant for places that need to write country (CF Pages
 * middleware-set cookie discovered post-mount, geo detect handler) without
 * subscribing to the atom value.
 */
export function useSetBrowsingCountry(): (next: string) => void {
  const setCountryAtom = useSetAtom(browsingCountryAtom);
  return useCallback(
    (next: string) => {
      const normalized = persistBrowsingCountry(next);
      setCountryAtom(normalized);
    },
    [setCountryAtom],
  );
}
